import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Section, Eyebrow, Headline, Lead } from '../../components/iss/IssPrimitives';
import type { OutcomeBand } from './scorecardQuestions';
import InitiationShell from './InitiationShell';
import './PocTypePage.scss';

type LocationState = {
  pocId?: string;
  pocType?: string;
  businessJustification?: Record<string, unknown>;
  scorecard?: {
    totalScore: number;
    band: OutcomeBand;
    bandTitle: string;
    bandMessage: string;
    answers: Record<string, boolean | undefined>;
    payerType: string | null;
  };
  scorecardLockMode?: 'SAVE' | 'SAVE_SUBMIT';
  resultAccepted?: boolean;
};

const ScorecardResultPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as LocationState | null) ?? {};
  const pocId = state.pocId;
  const scorecard = state.scorecard;

  const [accepted, setAccepted] = useState(false);
  const [locked, setLocked] = useState(false);
  const [savedMode, setSavedMode] = useState<'SAVE' | 'SAVE_SUBMIT' | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!scorecard) {
    return (
      <Section theme="sand" spacing="tight">
        <Eyebrow>POC Initiation</Eyebrow>
        <Headline as="h1" size="3xl">
          Scorecard result
        </Headline>
        <Lead>No scorecard data found. Please complete Step 3 first.</Lead>
        <button
          type="button"
          className="initiation__next"
          onClick={() => navigate('/pocs/new/scorecard', { state })}
        >
          Go to scorecard
        </button>
      </Section>
    );
  }

  const lockAndPersist = (mode: 'SAVE' | 'SAVE_SUBMIT') => {
    if (!accepted) {
      setError('Confirm that the information is truthful and you accept the score.');
      return;
    }
    setError(null);
    setLocked(true);
    setSavedMode(mode);
    // Mock persistence until backend APIs exist (no console logging of user data).
  };

  return (
    <InitiationShell activeStep="step4">
        <Section theme="light" spacing="tight">
          <Eyebrow>POC Initiation</Eyebrow>
          <Headline as="h1" size="3xl">
            Scorecard result
          </Headline>
          <Lead>Step 4 — Review your outcome and confirm.</Lead>

          <div className={'score-result score-result--' + scorecard.band.toLowerCase()}>
            <p className="score-result__label">Total score</p>
            <p className="score-result__score">{scorecard.totalScore}</p>
            <p className="score-result__band">{scorecard.bandTitle}</p>
            <p className="score-result__message">{scorecard.bandMessage}</p>
          </div>

          <label
            className={
              'score-result__confirm' + (locked ? ' score-result__confirm--locked' : '')
            }
          >
            <input
              type="checkbox"
              checked={accepted}
              disabled={locked}
              onChange={(e) => setAccepted(e.target.checked)}
            />
            <span>
              I confirm that the information I have provided is truthful and I accept
              the score.
            </span>
          </label>

          {error ? <p className="bj-field__error">{error}</p> : null}

          {locked ? (
            <p className="scorecard__notice" role="status">
              Fields are locked after{' '}
              {savedMode === 'SAVE_SUBMIT' ? 'Save & Submit' : 'Save'}.
              {savedMode === 'SAVE'
                ? ' Visible in My PoCs only.'
                : ' Visible in My PoCs and All PoCs.'}
            </p>
          ) : null}

          <div className="initiation__actions initiation__actions--wrap">
            <button
              type="button"
              className="initiation__link-back"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              disabled={locked}
              onClick={() => navigate('/pocs/new/scorecard', { state })}
            >
              ← Back to scorecard
            </button>

            <div className="score-result__buttons">
              <button
                type="button"
                className="initiation__next initiation__next--secondary"
                disabled={locked}
                onClick={() => lockAndPersist('SAVE')}
              >
                Save
              </button>
              <button
                type="button"
                className="initiation__next"
                disabled={locked}
                onClick={() => lockAndPersist('SAVE_SUBMIT')}
              >
                Save &amp; Submit
              </button>
            </div>
          </div>

          {locked ? (
            <div className="initiation__actions">
              <button
                type="button"
                className="initiation__next"
                onClick={() =>
                  navigate('/pocs/new/facilitation', {
                    state: {
                      ...state,
                      scorecardLockMode: savedMode,
                      resultAccepted: true,
                    },
                  })
                }
              >
                Continue to Step 5 →
              </button>
            </div>
          ) : null}
        </Section>
    </InitiationShell>
  );
};

export default ScorecardResultPage;
